import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';
import '../../style/dashboard.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PhonebookWiseLiveVoting = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Phonebook Wise Live Voting",
            name: "Name :",
            designation: "Designation :",
            mobile: "Mobile :",
            totalMember: "Total Members",
            totalVoting: "Total Voting",
            remainVoting: "Remain Voting",
            noData: "No data available.",
            loading: "Fetching Phonebook data...",
            searchPlaceholder: "Search by Name or Mobile No..."
        },
        hi: {
            pageTitle: "फोनबुक अनुसार लाइव वोटिंग",
            name: "नाम :",
            designation: "पद :",
            mobile: "मोबाइल :",
            totalMember: "कुल सदस्य",
            totalVoting: "कुल वोटिंग",
            remainVoting: "बाकी वोटिंग",
            noData: "कोई डेटा उपलब्ध नहीं है।",
            loading: "डेटा लोड हो रहा है...",
            searchPlaceholder: "नाम या मोबाइल नंबर से खोजें..."
        },
        gu: {
            pageTitle: "ફોનબુક વાઇઝ લાઈવ મતદાન",
            name: "નામ :",
            designation: "હોદ્દો :",
            mobile: "મોબાઇલ :",
            totalMember: "કુલ સભ્ય",
            totalVoting: "કુલ વોટિંગ",
            remainVoting: "બાકી વોટિંગ",
            noData: "કોઈ માહિતી ઉપલબ્ધ નથી.",
            loading: "માહિતી લોડ થઈ રહી છે...",
            searchPlaceholder: "નામ અથવા મોબાઇલ નંબર દ્વારા શોધો..."
        }
    };

    const txt = t[lang] || t.gu;

    const [loading, setLoading] = useState(false);
    const [phonebookData, setPhonebookData] = useState([]);
    const [colorThresholds, setColorThresholds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }

        const fetchPhonebookVotingData = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_phonebook_wise_voting_report`, {
                    app_id: appId
                });
                
                if (response.data.success && response.data.data) {
                    setPhonebookData(response.data.data.result || []);
                    
                    // Sort the color thresholds ascending by upto_percent for correct evaluation
                    const sortedColors = (response.data.data.result2 || []).sort((a, b) => a.upto_percent - b.upto_percent);
                    setColorThresholds(sortedColors);
                } else {
                    setPhonebookData([]);
                }
            } catch (error) {
                console.error("Error fetching phonebook voting report:", error);
                setPhonebookData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPhonebookVotingData();
        // Auto refresh every 60 seconds to keep live data fresh
        const interval = setInterval(fetchPhonebookVotingData, 60000);
        return () => clearInterval(interval);

    }, [appId, navigate]);

    // Function to determine the header color based on result2 array
    const getHeaderColor = (percent) => {
        // Default color if array is empty or percent is higher than max threshold
        let selectedColor = "#d68e09"; 
        
        for (let i = 0; i < colorThresholds.length; i++) {
            if (percent <= colorThresholds[i].upto_percent) {
                selectedColor = colorThresholds[i].color_code;
                break; // Stop at the first threshold met
            }
        }
        return selectedColor;
    };

    // Filter by search (name or mobile)
    const filteredData = phonebookData.filter(item => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        return (
            (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
            (item.mobile_no && item.mobile_no.toString().includes(lowerSearch))
        );
    });

    // Function to get initials for avatar
    const getInitials = (name) => {
        if (!name) return 'U';
        return name.charAt(0).toUpperCase();
    };

    // Function to get random color for avatar
    const getAvatarColor = (name) => {
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
        let hash = 0;
        if (name) {
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
        }
        return colors[Math.abs(hash) % colors.length];
    };

    return (
        <div className="pbw-live-voting-page">
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">
                
                {/* Search Bar */}
                <div className="search-wrapper mb-4 slide-up-element" style={{maxWidth:"500px"}}>
                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading && phonebookData.length === 0 ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2 text-muted">{txt.loading}</p>
                    </div>
                ) : filteredData.length > 0 ? (
                    <div className="row g-4 slide-up-element" style={{ animationDelay: '0.1s' }}>
                        {filteredData.map((item, index) => {
                            const headerColor = getHeaderColor(item.voting_percent);
                            const avatarColor = getAvatarColor(item.name);
                            const initials = getInitials(item.name);
                            
                            return (
                                <div className="col-12 col-sm-6 col-lg-6 col-xl-4" key={item.user_id || index} onClick={() => navigate("/dis_phonebook_wise_live_voting_voter", { state: { userId: item.user_id } })}>
                                    <div className="voting-card h-100">
                                        
                                        {/* Card Header (Dynamic Color) */}
                                        <div className="vc-header" style={{ backgroundColor: headerColor }}>
                                            <div className="vc-header-left">
                                                
                                               
                                                <div className="vc-header-info">
                                                    <h3 className="vc-name"> {index + 1}. {item.name}</h3>
                                                    <p className="vc-designation">{item.designation}</p>
                                                </div>
                                            </div>
                                            <div className="vc-percentage-pill">
                                                {item.voting_percent}%
                                            </div>
                                        </div>

                                        {/* Card Body (Stats) */}
                                        <div className="vc-body">
                                            <div className="vc-stat-box bg-light-blue">
                                                <span className="vc-lbl text-blue">{txt.totalMember}</span>
                                                <span className="vc-num text-blue">{item.total_phonebook_member}</span>
                                            </div>
                                            <div className="vc-stat-box bg-light-green">
                                                <span className="vc-lbl text-green">{txt.totalVoting}</span>
                                                <span className="vc-num text-green">{item.total_voting}</span>
                                            </div>
                                            <div className="vc-stat-box bg-light-red">
                                                <span className="vc-lbl text-red">{txt.remainVoting}</span>
                                                <span className="vc-num text-red">{item.total_remain}</span>
                                            </div>
                                        </div>

                                        {/* Card Footer (Mobile Number) */}
                                        {/* <div className="vc-footer">
                                            <div className="mobile-info">
                                                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                <span>{txt.mobile} {item.mobile_no}</span>
                                            </div>
                                        </div> */}

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center text-muted p-4 bg-white rounded shadow-sm border slide-up-element">
                        {txt.noData}
                    </div>
                )}
            </div>

            <style jsx>{`
                .pbw-live-voting-page { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* Search Bar */
                .search-wrapper { position: relative; width: 100%; }
                .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; }
                .search-input { width: 100%; padding: 14px 16px 14px 46px; border: 1px solid #cbd5e1; border-radius: 12px; font-size: 15px; color: #1e293b; background-color: #ffffff; outline: none; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

                /* Voting Card */
                .voting-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #cbd5e1;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease;
                    height: 100%;
                }
                .voting-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }

                /* Header */
                .vc-header {
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .vc-header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                }
                .serial-number-badge {
                    background: rgba(255, 255, 255, 0.25);
                    color: white;
                    font-weight: 800;
                    font-size: 14px;
                    min-width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    padding: 0 8px;
                }
                .avatar-circle {
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    font-weight: 700;
                    color: white;
                    text-transform: uppercase;
                    flex-shrink: 0;
                }
                .vc-header-info {
                    flex: 1;
                }
                .vc-name {
                    margin: 0;
                    color: white;
                    font-size: 1rem;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                    word-break: break-word;
                }
                .vc-designation {
                    margin: 4px 0 0 0;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 11px;
                    font-weight: 500;
                }
                .vc-percentage-pill {
                    background: white;
                    color: #1e293b;
                    font-weight: 800;
                    font-size: 14px;
                    padding: 4px 14px;
                    border-radius: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    white-space: nowrap;
                }

                /* Body / Stats */
                .vc-body {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 16px;
                    background: #ffffff;
                    flex: 1;
                }
                .vc-stat-box {
                    flex: 1;
                    border-radius: 10px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 16px 8px;
                    text-align: center;
                }

                /* Footer */
                .vc-footer {
                    padding: 12px 16px;
                    border-top: 1px solid #e2e8f0;
                    background: #f8fafc;
                }
                .mobile-info {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #475569;
                }

                /* Color Themes for Boxes */
                .bg-light-blue { background-color: #eff6ff; }
                .text-blue { color: #1e40af; }

                .bg-light-green { background-color: #f0fdf4; }
                .text-green { color: #166534; }

                .bg-light-red { background-color: #fef2f2; }
                .text-red { color: #991b1b; }

                /* Text formatting inside boxes */
                .vc-lbl {
                    font-size: 11px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                    text-align: center;
                }
                .vc-num {
                    font-size: 22px;
                    font-weight: 800;
                    line-height: 1;
                }

                /* Animation */
                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* Responsive Grid Breakpoints */
                @media (max-width: 768px) {
                    .vc-body { gap: 8px; padding: 12px; }
                    .vc-stat-box { padding: 12px 4px; }
                    .vc-lbl { font-size: 9px; }
                    .vc-num { font-size: 18px; }
                    .vc-header { padding: 12px 16px; }
                    .avatar-circle { width: 40px; height: 40px; font-size: 16px; }
                    .serial-number-badge { min-width: 28px; height: 28px; font-size: 12px; }
                    .vc-name { font-size: 0.9rem; }
                    .vc-percentage-pill { font-size: 11px; padding: 3px 10px; }
                    .vc-footer { padding: 10px 12px; }
                    .mobile-info { font-size: 11px; }
                }

                @media (max-width: 480px) {
                    .vc-stat-box { padding: 10px 2px; }
                    .vc-num { font-size: 16px; }
                    .vc-lbl { font-size: 8px; }
                    .avatar-circle { width: 36px; height: 36px; font-size: 14px; }
                    .serial-number-badge { min-width: 24px; height: 24px; font-size: 11px; }
                    .vc-name { font-size: 0.85rem; }
                    .vc-designation { font-size: 9px; }
                }
            `}</style>
        </div>
    );
};

export default PhonebookWiseLiveVoting;